(function () {
  'use strict';

  const DESKTOP_MIN_WIDTH = 1000;

  const LAYOUT2_SWIPER_SPEED = 500;
  const LAYOUT2_MOUSE_FALLBACK_DELAY = 80;


  /* =========================================================
     SHARED HELPERS
     ========================================================= */

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN_WIDTH;
  }

  function getBuilder() {
    return document.querySelector('#product-builder');
  }

  function getLayout(builder) {
    if (builder.classList.contains('cpb-thumb-layout-1')) return 1;
    if (builder.classList.contains('cpb-thumb-layout-2')) return 2;
    if (builder.classList.contains('cpb-thumb-layout-3')) return 3;

    return Number(window.cpbThumbnailLayout) || 1;
  }

  function getActiveSlide(wrapper) {
    if (!wrapper) return null;

    return (
      wrapper.querySelector('.swiper-slide-thumb-active') ||
      wrapper.querySelector('.swiper-slide-active') ||
      wrapper.querySelector('.swiper-slide-active-manual')
    );
  }


  /* =========================================================
     DESKTOP LAYOUT 1
     UNCHANGED
     ========================================================= */

  function initDesktopLayout1(builder) {
    const thumbs =
      builder.querySelector('.cpb-swiper-thumbnails');

    const container =
      thumbs?.querySelector('.swiper-container');

    const wrapper =
      thumbs?.querySelector('.swiper-wrapper');

    if (!thumbs || !container || !wrapper) {
      return false;
    }

    if (
      wrapper.dataset.cpbDesktopLayout1Initialised === 'true'
    ) {
      return true;
    }

    wrapper.dataset.cpbDesktopLayout1Initialised = 'true';

    let rafId = null;


    function getSlides() {
      return Array.from(
        wrapper.querySelectorAll('.swiper-slide')
      );
    }


    function updateFades() {
      const slides = getSlides();
      const active = getActiveSlide(wrapper);

      if (!slides.length || !active) {
        return;
      }

      const activeIndex =
        slides.indexOf(active);

      const lastIndex =
        slides.length - 1;

      const noFade =
        slides.length <= 1;

      const atStart =
        activeIndex <= 0;

      const atEnd =
        activeIndex >= lastIndex;

      thumbs.classList.toggle(
        'cpb-desktop-layout1-no-fade',
        noFade
      );

      thumbs.classList.toggle(
        'cpb-desktop-layout1-scrolled',
        !noFade && !atStart
      );

      thumbs.classList.toggle(
        'cpb-desktop-layout1-at-end',
        !noFade && atEnd
      );
    }


    function centreActiveThumbnail(
      animate = true
    ) {
      if (!isDesktop()) return;

      const active =
        getActiveSlide(wrapper);

      if (!active) return;

      const containerWidth =
        container.clientWidth;

      const activeCentre =
        active.offsetLeft +
        active.offsetWidth / 2;

      const targetX =
        containerWidth / 2 -
        activeCentre;

      wrapper.style.setProperty(
        'transition',
        animate
          ? 'transform 180ms ease'
          : 'none',
        'important'
      );

      wrapper.style.setProperty(
        '--desktop-layout1-thumb-x',
        `${targetX}px`
      );

      updateFades();
    }


    function scheduleUpdate(
      animate = true
    ) {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId =
        requestAnimationFrame(function () {
          rafId = null;

          centreActiveThumbnail(
            animate
          );
        });
    }


    const activeObserver =
      new MutationObserver(function (mutations) {
        let activeChanged = false;

        mutations.forEach(function (mutation) {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class'
          ) {
            activeChanged = true;
          }
        });

        if (activeChanged) {
          scheduleUpdate(true);
        }
      });


    function observeSlides() {
      getSlides().forEach(function (slide) {
        activeObserver.observe(
          slide,
          {
            attributes: true,
            attributeFilter: ['class']
          }
        );
      });
    }

    observeSlides();


    const childObserver =
      new MutationObserver(function (mutations) {
        let rebuilt = false;

        mutations.forEach(function (mutation) {
          if (
            mutation.type === 'childList'
          ) {
            rebuilt = true;
          }
        });

        if (!rebuilt) return;

        observeSlides();

        requestAnimationFrame(function () {
          centreActiveThumbnail(false);
        });
      });


    childObserver.observe(
      wrapper,
      {
        childList: true,
        subtree: false
      }
    );


    thumbs.addEventListener(
      'click',
      function () {
        setTimeout(function () {
          scheduleUpdate(true);
        }, 0);
      }
    );


    window.addEventListener(
      'resize',
      function () {
        if (!isDesktop()) return;

        scheduleUpdate(false);
      }
    );


    requestAnimationFrame(function () {
      centreActiveThumbnail(false);
    });


    console.log(
      '[CPB Desktop Thumbnails] Layout 1 initialised'
    );

    return true;
  }


  /* =========================================================
     DESKTOP LAYOUT 2
     500ms SWIPER
     MOUSE MISS FALLBACK
     ========================================================= */

  function setLayout2SwiperSpeed(builder) {
    builder
      .querySelectorAll('.swiper-container')
      .forEach(function (el) {
        const swiper = el.swiper;

        if (!swiper) return;

        swiper.params.speed =
          LAYOUT2_SWIPER_SPEED;

        if (swiper.originalParams) {
          swiper.originalParams.speed =
            LAYOUT2_SWIPER_SPEED;
        }
      });
  }


  function initDesktopLayout2(builder) {
    const thumbs =
      builder.querySelector('.cpb-swiper-thumbnails');

    const wrapper =
      thumbs?.querySelector('.swiper-wrapper');

    if (!thumbs || !wrapper) {
      return false;
    }

    setLayout2SwiperSpeed(builder);

    if (
      thumbs.dataset.cpbDesktopLayout2Initialised === 'true'
    ) {
      return true;
    }

    thumbs.dataset.cpbDesktopLayout2Initialised = 'true';

    const ACTIVE_REVEAL_GAP = 6;

    let rafId = null;


    function getSlides() {
      return Array.from(
        wrapper.querySelectorAll('.swiper-slide')
      );
    }


    function getActiveIndex() {
      const slides = getSlides();

      const active =
        getActiveSlide(wrapper);

      return active
        ? slides.indexOf(active)
        : -1;
    }


    function updateFades() {
      const maxScroll =
        thumbs.scrollHeight -
        thumbs.clientHeight;

      const hasOverflow =
        maxScroll > 2;

      const atStart =
        !hasOverflow ||
        thumbs.scrollTop <= 2;

      const atEnd =
        !hasOverflow ||
        thumbs.scrollTop >=
          maxScroll - 2;

      thumbs.classList.toggle(
        'cpb-desktop-layout2-thumbs-has-overflow',
        hasOverflow
      );

      thumbs.classList.toggle(
        'cpb-desktop-layout2-thumbs-scrolled',
        hasOverflow && !atStart
      );

      thumbs.classList.toggle(
        'cpb-desktop-layout2-thumbs-at-end',
        hasOverflow && atEnd
      );
    }


    function revealThumbnail(
      slide,
      behavior = 'smooth'
    ) {
      if (!slide) return;

      const maxScroll =
        thumbs.scrollHeight -
        thumbs.clientHeight;

      if (maxScroll <= 2) return;

      const railRect =
        thumbs.getBoundingClientRect();

      const slideRect =
        slide.getBoundingClientRect();

      const visibleTop =
        railRect.top +
        ACTIVE_REVEAL_GAP;

      const visibleBottom =
        railRect.bottom -
        ACTIVE_REVEAL_GAP;

      if (
        slideRect.top >= visibleTop &&
        slideRect.bottom <= visibleBottom
      ) {
        return;
      }

      if (
        slideRect.top <
        visibleTop
      ) {
        const difference =
          slideRect.top -
          visibleTop;

        const target =
          Math.max(
            0,
            thumbs.scrollTop +
            difference
          );

        thumbs.scrollTo({
          top: target,
          behavior
        });

        return;
      }

      if (
        slideRect.bottom >
        visibleBottom
      ) {
        const difference =
          slideRect.bottom -
          visibleBottom;

        const target =
          Math.min(
            maxScroll,
            thumbs.scrollTop +
            difference
          );

        thumbs.scrollTo({
          top: target,
          behavior
        });
      }
    }


    function scheduleFadeUpdate() {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId =
        requestAnimationFrame(function () {
          rafId = null;
          updateFades();
        });
    }


    /* Manual vertical scroll */

    thumbs.addEventListener(
      'scroll',
      scheduleFadeUpdate,
      { passive: true }
    );


    /* Reveal clicked thumbnail if clipped */

    thumbs.addEventListener(
      'click',
      function (event) {
        const slide =
          event.target.closest('.swiper-slide');

        if (
          !slide ||
          !wrapper.contains(slide)
        ) {
          return;
        }

        setTimeout(function () {
          revealThumbnail(
            slide,
            'smooth'
          );

          scheduleFadeUpdate();
        }, 0);
      }
    );


    /* =========================================================
       MOUSE MISS FALLBACK
       ========================================================= */

    thumbs.addEventListener(
      'click',
      function (event) {
        if (
          event.pointerType &&
          event.pointerType !== 'mouse'
        ) {
          return;
        }

        const slide =
          event.target.closest('.swiper-slide');

        if (
          !slide ||
          !wrapper.contains(slide)
        ) {
          return;
        }

        const slides =
          getSlides();

        const clickedIndex =
          slides.indexOf(slide);

        if (clickedIndex < 0) {
          return;
        }

        setTimeout(function () {
          if (
            getActiveIndex() ===
            clickedIndex
          ) {
            return;
          }

          console.log(
            '[CPB Layout 2] Mouse click missed — forcing thumbnail',
            clickedIndex + 1
          );

          const swiperContainer =
            thumbs.querySelector(
              '.swiper-container'
            );

          const thumbsSwiper =
            swiperContainer?.swiper;

          if (thumbsSwiper) {
            thumbsSwiper.slideTo(
              clickedIndex,
              LAYOUT2_SWIPER_SPEED
            );
          }

          const fallbackEvent =
            new PointerEvent(
              'pointerup',
              {
                bubbles: true,
                cancelable: true,
                pointerType: 'touch',
                pointerId: 1,
                isPrimary: true
              }
            );

          slide.dispatchEvent(
            fallbackEvent
          );

          requestAnimationFrame(function () {
            revealThumbnail(
              slide,
              'smooth'
            );

            scheduleFadeUpdate();
          });

        }, LAYOUT2_MOUSE_FALLBACK_DELAY);
      },
      true
    );


    /* =========================================================
       ACTIVE CHANGE OBSERVER
       ========================================================= */

    let lastActiveSlide =
      getActiveSlide(wrapper);


    const activeObserver =
      new MutationObserver(function (mutations) {
        let classChanged = false;

        mutations.forEach(function (mutation) {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class'
          ) {
            classChanged = true;
          }
        });

        if (!classChanged) return;

        const active =
          getActiveSlide(wrapper);

        if (!active) return;

        if (
          active === lastActiveSlide
        ) {
          return;
        }

        lastActiveSlide = active;

        requestAnimationFrame(function () {
          revealThumbnail(
            active,
            'smooth'
          );

          scheduleFadeUpdate();
        });
      });


    function observeSlides() {
      getSlides().forEach(function (slide) {
        activeObserver.observe(
          slide,
          {
            attributes: true,
            attributeFilter: ['class']
          }
        );
      });
    }

    observeSlides();


    /* CPB rebuild observer */

    const childObserver =
      new MutationObserver(function (mutations) {
        let rebuilt = false;

        mutations.forEach(function (mutation) {
          if (
            mutation.type === 'childList'
          ) {
            rebuilt = true;
          }
        });

        if (!rebuilt) return;

        observeSlides();

        requestAnimationFrame(function () {
          const active =
            getActiveSlide(wrapper);

          lastActiveSlide = active;

          if (active) {
            revealThumbnail(
              active,
              'auto'
            );
          }

          updateFades();
        });
      });


    childObserver.observe(
      wrapper,
      {
        childList: true,
        subtree: false
      }
    );


    window.addEventListener(
      'resize',
      function () {
        if (!isDesktop()) return;

        scheduleFadeUpdate();
      }
    );


    requestAnimationFrame(function () {
      const active =
        getActiveSlide(wrapper);

      lastActiveSlide = active;

      if (active) {
        revealThumbnail(
          active,
          'auto'
        );
      }

      updateFades();
    });


    console.log(
      '[CPB Desktop Thumbnails] Layout 2 initialised | 500ms | mouse fallback enabled'
    );

    return true;
  }


  /* =========================================================
     DESKTOP LAYOUT 3
     HORIZONTAL THUMBNAILS INSIDE PREVIEW
     ACTIVE ALWAYS CENTRED
     ========================================================= */

  function initDesktopLayout3(builder) {
    const thumbs =
      builder.querySelector('.cpb-swiper-thumbnails');

    const container =
      thumbs?.querySelector('.swiper-container');

    const wrapper =
      thumbs?.querySelector('.swiper-wrapper');

    if (!thumbs || !container || !wrapper) {
      return false;
    }

    if (
      wrapper.dataset.cpbDesktopLayout3Initialised === 'true'
    ) {
      return true;
    }

    wrapper.dataset.cpbDesktopLayout3Initialised = 'true';

    let rafId = null;


    function getSlides() {
      return Array.from(
        wrapper.querySelectorAll('.swiper-slide')
      );
    }


    function updateFades() {
      const slides =
        getSlides();

      const active =
        getActiveSlide(wrapper);

      if (
        !slides.length ||
        !active
      ) {
        return;
      }

      const activeIndex =
        slides.indexOf(active);

      const lastIndex =
        slides.length - 1;

      const noFade =
        slides.length <= 1;

      const atStart =
        activeIndex <= 0;

      const atEnd =
        activeIndex >= lastIndex;

      thumbs.classList.toggle(
        'cpb-desktop-layout3-no-fade',
        noFade
      );

      thumbs.classList.toggle(
        'cpb-desktop-layout3-scrolled',
        !noFade && !atStart
      );

      thumbs.classList.toggle(
        'cpb-desktop-layout3-at-end',
        !noFade && atEnd
      );
    }


    function centreActiveThumbnail(
      animate = true
    ) {
      if (!isDesktop()) return;

      const active =
        getActiveSlide(wrapper);

      if (!active) return;

      const containerWidth =
        container.clientWidth;

      const activeCentre =
        active.offsetLeft +
        active.offsetWidth / 2;

      const targetX =
        containerWidth / 2 -
        activeCentre;

      wrapper.style.setProperty(
        'transition',
        animate
          ? 'transform 180ms ease'
          : 'none',
        'important'
      );

      wrapper.style.setProperty(
        '--desktop-layout3-thumb-x',
        `${targetX}px`
      );

      updateFades();
    }


    function scheduleUpdate(
      animate = true
    ) {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId =
        requestAnimationFrame(function () {
          rafId = null;

          centreActiveThumbnail(
            animate
          );
        });
    }


    /* =========================================================
       ACTIVE CHANGE OBSERVER
       ========================================================= */

    let lastActiveSlide =
      getActiveSlide(wrapper);


    const activeObserver =
      new MutationObserver(function (mutations) {
        let classChanged = false;

        mutations.forEach(function (mutation) {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class'
          ) {
            classChanged = true;
          }
        });

        if (!classChanged) return;

        const active =
          getActiveSlide(wrapper);

        if (!active) return;

        if (
          active === lastActiveSlide
        ) {
          return;
        }

        lastActiveSlide = active;

        scheduleUpdate(true);
      });


    function observeSlides() {
      getSlides().forEach(function (slide) {
        activeObserver.observe(
          slide,
          {
            attributes: true,
            attributeFilter: ['class']
          }
        );
      });
    }

    observeSlides();


    /* =========================================================
       CPB REBUILD OBSERVER
       ========================================================= */

    const childObserver =
      new MutationObserver(function (mutations) {
        let rebuilt = false;

        mutations.forEach(function (mutation) {
          if (
            mutation.type === 'childList'
          ) {
            rebuilt = true;
          }
        });

        if (!rebuilt) return;

        observeSlides();

        requestAnimationFrame(function () {
          lastActiveSlide =
            getActiveSlide(wrapper);

          centreActiveThumbnail(false);
        });
      });


    childObserver.observe(
      wrapper,
      {
        childList: true,
        subtree: false
      }
    );


    /* =========================================================
       CLICK
       ========================================================= */

    thumbs.addEventListener(
      'click',
      function () {
        setTimeout(function () {
          const active =
            getActiveSlide(wrapper);

          if (active) {
            lastActiveSlide = active;
          }

          scheduleUpdate(true);
        }, 0);
      }
    );


    /* =========================================================
       RESIZE
       ========================================================= */

    window.addEventListener(
      'resize',
      function () {
        if (!isDesktop()) return;

        scheduleUpdate(false);
      }
    );


    /* =========================================================
       INITIAL POSITION
       ========================================================= */

    requestAnimationFrame(function () {
      lastActiveSlide =
        getActiveSlide(wrapper);

      centreActiveThumbnail(false);
    });


    console.log(
      '[CPB Desktop Thumbnails] Layout 3 initialised'
    );

    return true;
  }


  /* =========================================================
     INITIALISE
     ========================================================= */

  function initialise() {
    if (!isDesktop()) {
      return true;
    }

    const builder =
      getBuilder();

    if (!builder) {
      return false;
    }

    const layout =
      getLayout(builder);


    if (layout === 1) {
      return initDesktopLayout1(
        builder
      );
    }


    if (layout === 2) {
      return initDesktopLayout2(
        builder
      );
    }


    if (layout === 3) {
      return initDesktopLayout3(
        builder
      );
    }


    return true;
  }


  /* =========================================================
     START
     ========================================================= */

  function start() {
    if (initialise()) {
      return;
    }

    let attempts = 0;

    const interval =
      setInterval(function () {
        attempts += 1;

        if (
          initialise() ||
          attempts >= 60
        ) {
          clearInterval(interval);
        }
      }, 250);
  }


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

})();